import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { currency, PIPELINE, meta } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { Users, Search, Landmark, CheckCircle2, Car, ArrowRight, UserCheck, ChevronLeft, ChevronRight, CheckSquare, Trash2, Check, Inbox } from "lucide-react";

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [newMsgs, setNewMsgs] = useState(0);
  useEffect(() => { api.get("/contact/messages").then(({ data }) => setNewMsgs(data.filter((m) => (m.status || "new") === "new").length)).catch(() => {}); }, []);
  const [requests, setRequests] = useState(null);
  const [prequalQueue, setPrequalQueue] = useState([]);
  const [team, setTeam] = useState([]);
  const isRep = user?.role === "sales";
  const [assignee, setAssignee] = useState(isRep ? "mine" : "all");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const bulkDelete = async () => {
    const ids = [...selected];
    setConfirmBulk(false); exitSelect();
    setRequests((prev) => prev.filter((x) => !ids.includes(x.id)));
    try {
      await api.post("/requests/bulk-delete", { ids });
      toast.success(`${ids.length} deal${ids.length > 1 ? "s" : ""} moved to Trash`, { action: { label: "Undo", onClick: () => undoBulk(ids) } });
    } catch { toast.error("Couldn't delete deals"); api.get("/requests").then(({ data }) => setRequests(data)).catch(() => {}); }
  };
  const undoBulk = async (ids) => {
    try { await Promise.all(ids.map((id) => api.post(`/requests/${id}/restore`))); const { data } = await api.get("/requests"); setRequests(data); toast.success("Deals restored"); }
    catch { toast.error("Couldn't restore deals"); }
  };

  const refreshDashboard = useCallback(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/requests").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
    api.get("/prequal").then(({ data }) => setPrequalQueue(data)).catch(() => setPrequalQueue([]));
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshDashboard();
    window.addEventListener("focus", refreshDashboard);
    window.addEventListener("prequal-updated", refreshDashboard);
    return () => {
      window.removeEventListener("focus", refreshDashboard);
      window.removeEventListener("prequal-updated", refreshDashboard);
    };
  }, [refreshDashboard]);

  useEffect(() => {
    if (!user?.user_id) return;
    try { const s = JSON.parse(localStorage.getItem(`ab-pipe-collapsed:${user.user_id}`) || "[]"); if (Array.isArray(s) && s.length) setCollapsed(new Set(s)); } catch {}
  }, [user?.user_id]);

  const metrics = [
    { label: "Active deals", value: stats?.active, icon: Users, color: "text-rose-600 bg-rose-50" },
    { label: "Awaiting verification", value: stats?.pending_verify, icon: Search, color: "text-amber-700 bg-amber-50" },
    { label: "Financing in review", value: stats?.financing_pending, icon: Landmark, color: "text-purple-700 bg-purple-50" },
    { label: "Closed deals", value: stats?.closed, icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50" },
  ];

  const columns = PIPELINE.filter((s) => !["closed", "prequal", "unavailable"].includes(s));
  const matchesAssignee = (r) => assignee === "all" || (assignee === "unassigned" ? !r.assignee_id : assignee === "mine" ? r.assignee_id === user?.user_id : r.assignee_id === assignee);
  const byStatus = (s) => (requests || []).filter((r) => {
    const effectiveStatus = r.status === "prequal" ? "requested" : r.status;
    return effectiveStatus === s && matchesAssignee(r);
  });
  const persistCollapsed = (set) => { if (user?.user_id) { try { localStorage.setItem(`ab-pipe-collapsed:${user.user_id}`, JSON.stringify([...set])); } catch {} } };
  const toggleCol = (col) => setCollapsed((prev) => { const n = new Set(prev); n.has(col) ? n.delete(col) : n.add(col); persistCollapsed(n); return n; });
  const collapseEmpty = () => setCollapsed((prev) => { const n = new Set([...prev, ...columns.filter((c) => byStatus(c).length === 0)]); persistCollapsed(n); return n; });
  const expandAll = () => { const n = new Set(); setCollapsed(n); persistCollapsed(n); };
  const doMove = async (id, col, prevStatus) => {
    setRequests((prev) => prev.map((x) => (x.id === id ? { ...x, status: col } : x)));
    try {
      await api.patch(`/requests/${id}/status`, { status: col, note: "" });
      toast.success(`Moved to ${meta(col).label}`, { action: { label: "Undo", onClick: () => undoMove(id, prevStatus) } });
    } catch {
      setRequests((prev) => prev.map((x) => (x.id === id ? { ...x, status: prevStatus } : x)));
      toast.error("Couldn't move deal");
    }
  };
  const undoMove = async (id, prevStatus) => {
    setRequests((prev) => prev.map((x) => (x.id === id ? { ...x, status: prevStatus } : x)));
    try { await api.patch(`/requests/${id}/status`, { status: prevStatus, note: "" }); toast.success(`Moved back to ${meta(prevStatus).label}`); }
    catch { toast.error("Couldn't undo"); }
  };
  const handleDrop = (e, col) => {
    e.preventDefault(); setDragOverCol(null);
    const id = e.dataTransfer.getData("text/plain") || dragId; setDragId(null);
    const r = (requests || []).find((x) => x.id === id);
    if (!r || r.status === col) return;
    if (col === "unavailable") { setPendingMove({ id, col, prevStatus: r.status }); return; }
    doMove(id, col, r.status);
  };

  const myCount = (requests || []).filter((r) => r.assignee_id === user?.user_id).length;
  const unassignedCount = (requests || []).filter((r) => !r.assignee_id).length;

  const prequalCounts = (prequalQueue || []).reduce((acc, prequal) => {
    const status = prequal.status;
    if (status && status !== "none") acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const prequalTabs = [
    { key: "all", label: "All prequals" },
    { key: "pending", label: "Needs review" },
    { key: "prequalified", label: "Prequalified" },
    { key: "declined", label: "Declined" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-[1600px] px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Broker dashboard</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-300">Lead pipeline · verification · quotes · financing · fulfillment</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`metric-${m.label}`}>
              <div className={`flex h-10 w-10 items-center justify-center rounded-md ${m.color}`}><m.icon className="h-5 w-5" strokeWidth={2.5} /></div>
              <div className="mt-3 font-display text-3xl font-black text-slate-950 dark:text-slate-50">{stats ? (m.value ?? 0) : <Skeleton className="h-8 w-12" />}</div>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{m.label}</p>
            </div>
          ))}
        </div>

        <section className="mt-8 border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900" data-testid="dashboard-prequal-group">
          <p className="text-xs font-bold uppercase tracking-wide text-rose-600">Prequalification group</p>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">Buyer review queue</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="dashboard-prequal-source-note">Review the same full buyer forms as the Broker Admin Prequal page.</p>
            </div>
            <button onClick={() => navigate("/staff/prequal?status=all")} data-testid="open-all-prequals" className="text-sm font-bold text-rose-600 hover:text-rose-700">Open all prequals <ArrowRight className="ml-1 inline h-4 w-4" /></button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="prequal-filter">
          {prequalTabs.filter((t) => t.key !== "all").map((t) => {
            const count = t.key === "all" ? prequalQueue.length : (prequalCounts[t.key] || 0);
            return (
              <button key={t.key} data-testid={`prequal-tab-${t.key}`} onClick={() => navigate(`/staff/prequal?status=${t.key}`)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {t.label}
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-slate-900 dark:text-slate-400">{count}</span>
              </button>
            );
          })}
          </div>
        </section>
        <h2 className="mt-10 font-display text-xl font-bold text-slate-900 dark:text-slate-100">Lead pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {team.length > 0 && (
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="assignee-filter"
              className="ml-auto rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300">
              <option value="all">All assignees</option>
              <option value="mine">My deals</option>
              <option value="unassigned">Unassigned</option>
              {team.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
            </select>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setAssignee(assignee === "mine" ? "all" : "mine")} data-testid="my-deals-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${assignee === "mine" ? "border-rose-600 bg-rose-600 text-white" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
            <UserCheck className="h-3.5 w-3.5" /> My deals
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${assignee === "mine" ? "bg-white/25" : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"}`}>{myCount}</span>
          </button>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700" data-testid="unassigned-badge">
            Unassigned<span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px]">{unassignedCount}</span>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="select-mode-toggle"
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${selectMode ? "border-rose-600 bg-rose-600 text-white" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
              <CheckSquare className="h-3.5 w-3.5" /> {selectMode ? "Cancel select" : "Select"}
            </button>
            <button onClick={() => navigate("/staff/messages")} data-testid="messages-link"
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${newMsgs ? "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-rose-300"}`}>
              <Inbox className="h-3.5 w-3.5" /> Messages{newMsgs ? <span className="rounded-full bg-rose-600 px-1.5 text-[10px] text-white" data-testid="messages-new-count">{newMsgs}</span> : null}
            </button>
            <button onClick={() => navigate("/staff/trash")} data-testid="trash-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors hover:border-rose-300">
              <Trash2 className="h-3.5 w-3.5" /> Trash
            </button>
          </div>
        </div>
        {selectMode && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 dark:border-rose-900/50 dark:bg-rose-950/20" data-testid="bulk-action-bar">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200" data-testid="bulk-selected-count">{selected.size} selected</span>
            <button onClick={() => setConfirmBulk(true)} disabled={selected.size === 0} data-testid="bulk-delete-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-40">
              <Trash2 className="h-3.5 w-3.5" /> Delete selected
            </button>
            {selected.size > 0 && <button onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn" className="text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400">Clear</button>}
            <span className="ml-auto text-[11px] font-medium text-slate-500 dark:text-slate-400">Click cards to select · deals move to Trash (restorable)</span>
          </div>
        )}
        {requests !== null && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="pipeline-counts-bar">
            {columns.map((col) => {
              const c = byStatus(col).length;
              const isCol = collapsed.has(col);
              return (
                <button key={col} data-testid={`count-chip-${col}`} onClick={() => toggleCol(col)}
                  title={`${meta(col).label}: ${c} · click to ${isCol ? "expand" : "collapse"}`}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${isCol ? "border-dashed border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-500" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:border-rose-300"}`}>
                  {meta(col).label}
                  <span className={`rounded px-1.5 py-0.5 text-[10px] ${c ? "bg-rose-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500"}`}>{c}</span>
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-1.5">
              <button data-testid="collapse-empty-btn" onClick={collapseEmpty} className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors hover:border-rose-300">Collapse empty</button>
              {collapsed.size > 0 && <button data-testid="expand-all-btn" onClick={expandAll} className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-600 transition-colors hover:bg-rose-100">Expand all ({collapsed.size})</button>}
            </div>
          </div>
        )}
        {requests === null ? (
          <div className="mt-4 flex gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 w-72 rounded-lg" />)}</div>
        ) : (
          <div className="mt-3 grid gap-4 pb-4 lg:flex lg:gap-2" data-testid="kanban-board">
            {columns.map((col) => {
              const items = byStatus(col);
              const m = meta(col);
              if (collapsed.has(col)) {
                return (
                  <button key={col} data-testid={`kanban-col-${col}`} onClick={() => toggleCol(col)} title={`Expand ${m.label}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOverCol(col); }}
                    onDragLeave={() => setDragOverCol((c) => (c === col ? null : c))}
                    onDrop={(e) => handleDrop(e, col)}
                    className={`flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-100/60 px-3 transition-colors hover:border-rose-300 dark:border-slate-800 dark:bg-slate-900/60 lg:h-auto lg:w-10 lg:shrink-0 lg:flex-col lg:px-0 lg:py-3 ${dragOverCol === col ? "ring-2 ring-rose-400" : ""}`}>
                    <span className="rounded-full bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold text-slate-600 dark:text-slate-300">{items.length}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:[writing-mode:vertical-rl]">{m.label}</span>
                  </button>
                );
              }
              return (
                <div key={col} data-testid={`kanban-col-${col}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverCol(col); }}
                  onDragLeave={() => setDragOverCol((c) => (c === col ? null : c))}
                  onDrop={(e) => handleDrop(e, col)}
                  className={`w-full min-w-0 rounded-lg lg:w-auto lg:flex-1 ${dragOverCol === col ? "ring-2 ring-rose-400 ring-offset-2 dark:ring-offset-slate-950" : ""}`}>
                  <div className="mb-3 flex items-center justify-between gap-1">
                    <span className="truncate text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{m.label}</span>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="rounded-full bg-slate-200 dark:bg-slate-800 px-2 py-0.5 text-xs font-bold text-slate-600 dark:text-slate-300">{items.length}</span>
                      <button data-testid={`collapse-col-${col}`} onClick={() => toggleCol(col)} title="Collapse column" className="rounded p-0.5 text-slate-400 transition-colors hover:text-rose-600"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {items.map((r) => {
                      const sel = selected.has(r.id);
                      return (
                      <div key={r.id} data-testid={`kanban-card-${r.id}`} draggable={!selectMode}
                        onDragStart={(e) => { e.dataTransfer.setData("text/plain", r.id); e.dataTransfer.effectAllowed = "move"; setDragId(r.id); }}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => (selectMode ? toggleSelect(r.id) : navigate(`/staff/requests/${r.id}`))}
                        className={`group relative cursor-pointer rounded-lg border bg-white dark:bg-slate-900 p-3 transition-all hover:border-rose-300 ${sel ? "border-rose-500 ring-2 ring-rose-400" : "border-slate-200 dark:border-slate-800"} ${dragId === r.id ? "opacity-50" : ""}`}>
                        {selectMode && (
                          <span data-testid={`card-checkbox-${r.id}`} className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded border ${sel ? "border-rose-600 bg-rose-600 text-white" : "border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"}`}>
                            {sel && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </span>
                        )}
                        <p className="truncate font-display text-sm font-bold text-slate-900 dark:text-slate-100">{r.year} {r.make} {r.model}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{r.buyer_name || r.buyer_email}</p>
                        {r.assignee_name && <p className="mt-0.5 truncate text-[11px] font-semibold text-rose-600" data-testid={`card-assignee-${r.id}`}>● {r.assignee_name}</p>}
                        {r.prequalification_status && r.prequalification_status !== "none" && (() => {
                          const status = r.prequalification_status;
                          const tone = status === "prequalified" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : status === "pending" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-red-200 bg-red-50 text-red-700";
                          const label = status === "prequalified" ? "Prequalified" : status === "pending" ? "Prequal review" : "Prequal declined";
                          return <span data-testid={`card-prequal-status-${r.id}`} className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${tone}`}>{label}</span>;
                        })()}
                        {r.prequalification_status && r.prequalification_status !== "none" && <button onClick={(event) => { event.stopPropagation(); navigate(`/staff/prequal?buyer_id=${encodeURIComponent(r.buyer_id)}`); }} data-testid={`card-open-prequal-${r.id}`} className="mt-2 flex w-full items-center justify-between gap-2 border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-left text-[11px] font-bold text-amber-800 transition-colors hover:border-amber-400 hover:bg-amber-100"><span>Open prequal review</span><ArrowRight className="h-3.5 w-3.5 shrink-0" /></button>}
                        {r.financing?.prequal?.signal && (() => {
                          const p = r.financing.prequal;
                          const s = { likely: "bg-emerald-50 text-emerald-700 border-emerald-200", possible: "bg-amber-50 text-amber-700 border-amber-200", needs_review: "bg-red-50 text-red-700 border-red-200" }[p.signal];
                          const lbl = { likely: "Likely", possible: "Possible", needs_review: "Needs review" }[p.signal];
                          return <span data-testid={`card-prequal-${r.id}`} className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${s}`}>{lbl}{p.dti != null ? ` · DTI ${p.dti}%` : ""}</span>;
                        })()}
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{r.quote ? currency(r.quote.total) : r.msrp ? currency(r.msrp) : "No quote"}</span>
                          <span className="text-[10px] font-medium uppercase text-slate-400 dark:text-slate-500">{r.source === "dealer_link" ? "Link" : "Inv"}</span>
                        </div>
                      </div>
                      );
                    })}
                    {items.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 py-6 text-center text-xs text-slate-300">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AlertDialog open={!!pendingMove} onOpenChange={(o) => { if (!o) setPendingMove(null); }}>
        <AlertDialogContent data-testid="unavailable-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this deal Unavailable?</AlertDialogTitle>
            <AlertDialogDescription>
              Moving a deal to “Unavailable” signals the vehicle can no longer be sourced and notifies the buyer. Confirm this isn’t an accidental drop — you can always drag it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unavailable-cancel">Keep in {pendingMove ? meta(pendingMove.prevStatus).label : ""}</AlertDialogCancel>
            <AlertDialogAction data-testid="unavailable-confirm" onClick={() => { const p = pendingMove; setPendingMove(null); if (p) doMove(p.id, p.col, p.prevStatus); }} className="bg-red-600 text-white hover:bg-red-700">Mark Unavailable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move {selected.size} deal{selected.size > 1 ? "s" : ""} to Trash?</AlertDialogTitle>
            <AlertDialogDescription>
              These deals will be moved to Trash and hidden from the pipeline. You can restore them anytime, or delete them permanently from Trash.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Keep deals</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-delete-confirm" onClick={bulkDelete} className="bg-red-600 text-white hover:bg-red-700">Move to Trash</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
